import React, {useCallback, useEffect} from "react";
import {AgGridReact} from "ag-grid-react";

import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import {useLoadingSpinnerStore} from "src/store/util/loadingSpinnerUtil";
import {
    deleteMST4050_R01Header,
    downloadExcel_MST4050_R01,
    downloadReportPdf_MST4050_R01,
    getMST4050_R01DetailAllCount,
    getMST4050_R01DetailList,
    getMST4050_R01HeaderAllCount,
    getMST4050_R01HeaderList,
    saveMST4050_R01,
} from "./Api_MST4050_R01";

import {showError, showSuccess, ShowSuccessFetch, showWarning} from "src/utils/toast/toastUtil";
import {
    defaultMST4050_R01DetailDto,
    defaultMST4050_R01HeaderDto,
    MST4050_R01DetailDto,
    MST4050_R01HeaderDto
} from "./Dto_MST4050_R01";
import {defaultMST4050_R01Filter, MST4050_R01Filter, useMST4050_R01Store} from "./Store_MST4050_R01";
import {gridRefSelectedRowHandler} from "src/utils/grid/GridHandlerContent";
import {refetchUpdateList} from "src/utils/axios/reFetch";
import {downloadSettingExcel} from "src/utils/blob/blobUtil";
import {formatDate} from "src/utils/string/stringUtil";
import {useCommonCodeStore} from "src/store/useCommonCodeStore";
import useUserStore from "src/store/useUserStore";
import useUiuxStore from "src/store/useUiuxStore";
import { useQzPrint } from "src/hooks/useQzPrint";
import qz from "qz-tray";

interface Props {
	gridRef: React.RefObject<AgGridReact<MST4050_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<any> | null>;
	setMessage: (message: string) => void;
	containerRef: React.RefObject<HTMLDivElement | null>;
	isResizing: React.MutableRefObject<boolean>;
}

// MST4050_R01의 조회·저장·삭제·출력과 화면 초기화 동작을 제공한다.
export const Content_MST4050_R01 = (props: Props) => {

	const {
		gridRef,
		setMessage,
	} = props;

	const {
		MST4050_R01Filter, setMST4050_R01Filter,
		setMST4050_R01PastFilter,
		MST4050_R01List, setMST4050_R01List,
		selectedMST4050_R01, setSelectedMST4050_R01,
		editMST4050_R01, setEditMST4050_R01,
		MST4050_R01DetailList, setMST4050_R01DetailList,
		insertMST4050_R01DetailList,
		deletedMST4050_R01DetailList,
		setDeletedMST4050_R01DetailList,
		selectedMST4050_R01DetailList,
		setSelectedMST4050_R01DetailList,
		originalMST4050_R01DetailList,
		setOriginalMST4050_R01DetailList,
		setAllCount,
		setDetailAllCount,
		baseQty, setBaseQty,
		detailFetchTrigger, setDetailFetchTrigger,
		initData,
		setScrollTop,
		setDetailScrollTop,
	} = useMST4050_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { printPdfFromBlob } = useQzPrint();
	const { userInfo } = useUserStore();
	const { setConfirmModalProps, setConfirmModalVisible } = useUiuxStore();
	const { chemicalTypeList, eqmStatusList, compCdList, orgCdList, unitList } = useCommonCodeStore();

	useFetchActionbarAuth("MST4050_R01");

	// 상세 수량 합계를 계산해 Info의 보유량을 저장 전에도 실시간으로 갱신한다.
	useEffect(() => {
		const current = useMST4050_R01Store.getState().editMST4050_R01;
		const hasDifferentEquipment = MST4050_R01DetailList.some(
			detail => detail.eqmCd && detail.eqmCd !== current.equipmentCd,
		);
		if (hasDifferentEquipment) return;

		const deletedIds = new Set(deletedMST4050_R01DetailList.map(String));
        const nextEqmQty = MST4050_R01DetailList.reduce((sum, detail) => {
            if (deletedIds.has(String(detail.valueIndex))) return sum;
            const qty = Number(detail.transportQty) || 0;
            if (detail.inspectType === "INCOMING") return sum + qty;
            if (detail.inspectType === "RELEASE") return sum - qty;
            return sum;
        }, baseQty);

		if (current.eqmQty !== nextEqmQty) {
			setEditMST4050_R01({ ...current, eqmQty: nextEqmQty });
		}
	}, [MST4050_R01DetailList, deletedMST4050_R01DetailList, baseQty, setEditMST4050_R01]);

	/**
	 * 엑셀 다운로드 (선택 약품의 헤더 + 입출고 내역)
	 */
	const handleExcelClick = useCallback(async () => {
		if (!selectedMST4050_R01?.equipmentCd) {
			showWarning('엑셀 다운로드할 약품을 선택해주세요.');
			return;
		}

		setLoadingSpinner(true);

		try {
			const response = await downloadExcel_MST4050_R01(selectedMST4050_R01.equipmentCd);
			const fileName = `약품_${selectedMST4050_R01.equipmentNm}_입출고내역.xlsx`;
			downloadSettingExcel({ responseData: response.data.data, fileName });
			showSuccess('엑셀 다운로드가 완료되었습니다.');
		} catch (error: any) {
			console.error("엑셀 다운로드 중 오류 발생:", error);
			showError(error.response?.data?.message || "엑셀 다운로드 중 오류가 발생했습니다.");
		}

		setLoadingSpinner(false);
	}, [selectedMST4050_R01, setLoadingSpinner]);

	// 출력: QZ Tray 연결 시 보고서1 프린터로 직접 출력하고, 미연결 또는 실패 시 새 창으로 연다.
	const handlePrintClick = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const response = await downloadReportPdf_MST4050_R01(
				getParams(MST4050_R01Filter),
			);
			const blob = new Blob([response.data], { type: "application/pdf" });

			if (qz.websocket.isActive()) {
				const printed = await printPdfFromBlob(blob, "report1");
				if (printed) {
					showSuccess("보고서 출력을 완료하였습니다.");
					return;
				}
			}

			const fileUrl = URL.createObjectURL(blob);
			window.open(fileUrl, "_blank");
			setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
			showSuccess("QZ Tray를 사용할 수 없어 PDF를 새 탭에서 열었습니다.");
		} catch (error: any) {
			console.error("보고서 출력 중 오류 발생:", error);
			showError(error.response?.data?.message || "보고서 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [MST4050_R01Filter, printPdfFromBlob, setLoadingSpinner]);

	// 빈 조회조건은 제외하고 서버에 전달할 검색 파라미터만 구성한다.
	const getParams = (filter: MST4050_R01Filter) => {
		const params: Partial<MST4050_R01Filter> = {};
		if (filter.compCd) params.compCd = filter.compCd;
		if (filter.equipmentType) params.equipmentType = filter.equipmentType;
		if (filter.equipmentCd) params.equipmentCd = filter.equipmentCd;
		if (filter.eqmStatus) params.eqmStatus = filter.eqmStatus;
		if (filter.searchYm) params.searchYm = filter.searchYm;
		return params;
	}

	// 현재 검색조건 기준의 약품 전체 건수를 다시 조회한다.
	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getMST4050_R01HeaderAllCount(getParams(MST4050_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [MST4050_R01Filter, setAllCount]);

	/**
	 * 헤더 목록 조회 (의약품 목록)
	 */
	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);
		setDetailScrollTop(null);

		try {
			const response = await getMST4050_R01HeaderList(
				'search',
				getParams(MST4050_R01Filter),
				MST4050_R01List.length ?? 0,
				0
			);

			let newList = [...response.data.data] as MST4050_R01HeaderDto[];
			setMST4050_R01List(newList);

			const processAllCount = await renewalAllCount();

			if (newList && newList.length > 0) {
				gridRefSelectedRowHandler(
					gridRef,
					setSelectedMST4050_R01,
					() => true
				);
				setMST4050_R01PastFilter(MST4050_R01Filter);
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			} else {
				showWarning('조회된 데이터가 없습니다.');
				handleCreateClick();
			}
		} catch (error: any) {
			console.error("조회 중 오류 발생:", error);
			showError(error.response?.data?.message || "조회 중 오류가 발생했습니다.");
			setSelectedMST4050_R01(undefined);
			setMST4050_R01DetailList([]);
		}

		setLoadingSpinner(false);
		setTimeout(() => setMessage(""), 5000);
	}, [
		MST4050_R01Filter,
		MST4050_R01List,
		gridRef,
		setMST4050_R01List,
		setSelectedMST4050_R01,
		setMST4050_R01DetailList,
		setMST4050_R01PastFilter,
		renewalAllCount,
		setMessage,
		setLoadingSpinner,
		setScrollTop,
		setDetailScrollTop,
	]);

	/**
	 * 디테일 목록 조회 (입출고 내역)
	 */
	const fetchDetailData = useCallback(async (eqmCd: string) => {
		if (!detailFetchTrigger) return;
		if (!eqmCd) {
			setMST4050_R01DetailList([]);
			setBaseQty(0);
			return;
		}

		setLoadingSpinner(true);

		try {
			const detailParams = { ...MST4050_R01Filter, eqmCd };
			const detailResponse = await getMST4050_R01DetailList(
				'search', detailParams, 0, 0,
			);

			const detailList = [...(detailResponse.data.data.detailDtoList ?? [])];
			setMST4050_R01DetailList(detailList);
			setOriginalMST4050_R01DetailList(detailList);
			setBaseQty(detailResponse.data.data.baseQty ?? 0);

			const detailCountResponse = await getMST4050_R01DetailAllCount({
				...detailParams
			});
			setDetailAllCount(detailCountResponse.data.data);

			if (detailList.length > 0) {
				setMessage(`${selectedMST4050_R01?.equipmentNm || eqmCd}의 입출고 내역 ${detailList.length}건을 조회하였습니다.`);
				setTimeout(() => setMessage(""), 5000);
			} else {
				setMST4050_R01DetailList([]);
				insertMST4050_R01DetailList([{ ...defaultMST4050_R01DetailRow(), inserted: true }]);
				setMessage(`${selectedMST4050_R01?.equipmentNm || eqmCd}의 입출고 내역이 없습니다. 새로 추가할 수 있습니다.`);
				setTimeout(() => setMessage(""), 3000);
			}
		} catch (error: any) {
			console.error("입출고 내역 조회 중 오류 발생:", error);
			showError(error.response?.data?.message || "입출고 내역 조회 중 오류가 발생했습니다.");
			setMST4050_R01DetailList([]);
			setBaseQty(0);
			insertMST4050_R01DetailList([{ ...defaultMST4050_R01DetailRow(), inserted: true }]);
		}

		setLoadingSpinner(false);
		setDetailFetchTrigger(false);
	}, [
		MST4050_R01Filter,
		selectedMST4050_R01,
		setMST4050_R01DetailList,
		setOriginalMST4050_R01DetailList,
		setBaseQty,
		setDetailAllCount,
		setMessage,
		setLoadingSpinner,
	]);

	// 공통코드의 첫 값을 반영한 신규 의약품 기본 행을 만든다.
	const defaultMST4050_R01HeaderRow = useCallback(() => {
		return {
			...defaultMST4050_R01HeaderDto,
			equipmentType: chemicalTypeList[0]?.minorCd ?? '',
			eqmUnit: unitList[0]?.minorCd ?? '',
			eqmStatus: eqmStatusList[0]?.minorCd ?? '',
			compCd: compCdList[0]?.compCd ?? '',
			eqmOrgcd: orgCdList[0]?.orgCd ?? '',
		};
	}, [chemicalTypeList, unitList, eqmStatusList, compCdList, orgCdList]);

	// 로그인 사용자와 공통코드 기본값을 반영한 신규 입출고 행을 만든다.
	const defaultMST4050_R01DetailRow = useCallback(() => {
		return {
			...defaultMST4050_R01DetailDto,
			inspectType: 'INCOMING',
			insertUser: userInfo?.empNo ?? '',
			insertUserNm: userInfo?.empNm ?? '',
			inserted: true,
		}
	}, [userInfo?.empNo, userInfo?.empNm]);

	/**
	 * 초기화
	 */
	const initContainerData = useCallback(() => {
		initData();
		setMST4050_R01Filter({ ...defaultMST4050_R01Filter });
	}, [initData, setMST4050_R01Filter]);

	/**
	 * 신규
	 */
	const handleCreateClick = useCallback(() => {
		setDeletedMST4050_R01DetailList([]);
		setSelectedMST4050_R01DetailList([]);
		setOriginalMST4050_R01DetailList([]);
		setMST4050_R01DetailList([{ ...defaultMST4050_R01DetailRow() }]);
		setEditMST4050_R01({ ...defaultMST4050_R01HeaderRow() });
		setSelectedMST4050_R01(undefined);
	}, [
		defaultMST4050_R01HeaderRow,
		defaultMST4050_R01DetailRow,
		setSelectedMST4050_R01,
		setEditMST4050_R01,
		setMST4050_R01DetailList,
		setDeletedMST4050_R01DetailList,
		setSelectedMST4050_R01DetailList,
		setOriginalMST4050_R01DetailList
	]);

	/**
	 * 선택한 의약품과 입출고 내역 삭제
	 */
	const handleDeleteClick = useCallback(() => {
		const equipmentCd = selectedMST4050_R01?.equipmentCd;
		if (!equipmentCd) {
			showWarning('삭제할 의약품을 선택해주세요.');
			return;
		}

		// 확인창 상태와 props를 함께 정리해 다음 확인창에 값이 남지 않게 한다.
		const closeConfirm = () => {
			setConfirmModalVisible(false);
			setConfirmModalProps(undefined);
		};

		// 확인 후 서버에서 의약품과 연결 입출고 내역을 트랜잭션으로 삭제한다.
		const deleteDrug = async () => {
			closeConfirm();
			setLoadingSpinner(true);

			try {
				const response = await deleteMST4050_R01Header(equipmentCd);
				if (!response.data.successFlag) {
					showError(response.data.message || '의약품 삭제 중 오류가 발생했습니다.');
					return;
				}

				setMST4050_R01List(MST4050_R01List.filter((row) => row.equipmentCd !== equipmentCd));
				handleCreateClick();
				await renewalAllCount();

				const successMessage = `${equipmentCd} 의약품과 입출고 내역이 삭제되었습니다.`;
				showSuccess(successMessage);
				setMessage(successMessage);
				setTimeout(() => setMessage(''), 5000);
			} catch (error: any) {
				console.error('의약품 삭제 중 오류 발생:', error);
				showError(error.response?.data?.message || '의약품 삭제 중 오류가 발생했습니다.');
			} finally {
				setLoadingSpinner(false);
			}
		};

		setConfirmModalProps({
			title: '의약품 삭제',
			message: '입출고 내역까지 삭제됩니다.\n삭제하시겠습니까?',
			confirmText: '삭제',
			cancelText: '취소',
			onConfirm: () => { void deleteDrug(); },
			onCancel: closeConfirm,
		});
		setConfirmModalVisible(true);
	}, [
		selectedMST4050_R01,
		MST4050_R01List,
		setMST4050_R01List,
		handleCreateClick,
		renewalAllCount,
		setMessage,
		setLoadingSpinner,
		setConfirmModalProps,
		setConfirmModalVisible,
	]);

	/**
	 * 저장: 통합 저장 API 1회 호출 (헤더 + 디테일, 단일 트랜잭션)
	 */
	const handleSaveClick = useCallback(async () => {
		if (!editMST4050_R01?.equipmentCd) {
			showWarning('약품코드를 입력해주세요.');
			return;
		}

		setLoadingSpinner(true);

		try {
			const deletedValueIndexList = new Set(deletedMST4050_R01DetailList.map(String));

			// 그리드 상태 필드는 제외하고 서버 비교에 필요한 현재 값과 원본 키만 구성한다.
			const buildDetailDto = (detail: MST4050_R01DetailDto): MST4050_R01DetailDto => {
                const originalDetail = originalMST4050_R01DetailList.find(
                    (orig) => orig.valueIndex === detail.valueIndex,
                );
				const { inserted, isModified, deleted, ...detailData } = detail;

                return {
					...detailData,
                    eqmCd: editMST4050_R01.equipmentCd,
					compCd: editMST4050_R01.compCd,
                    inspectCycle: detail.inspectCycle ?? '',
                    inspectDt: formatDate(detail.inspectDt),
                    inspectTime: detail.inspectTime || undefined as any,		// '' → 미전송 (백엔드 00:00:00 처리)
                    oldInspectDt: originalDetail?.inspectDt,
                    oldEqmCd: originalDetail?.eqmCd,
                    oldInspectCycle: originalDetail?.inspectCycle,
                    oldInspectType: originalDetail?.inspectType,
				} as MST4050_R01DetailDto;
            };

			// 수량이 0인 행과 삭제 표시된 행은 제외하고 유효한 상세만 전송한다.
			const detailDtoList = MST4050_R01DetailList
				.filter((detail) =>
					!detail.isBaseRow
					&& Number(detail.transportQty) > 0
					&& !deletedValueIndexList.has(String(detail.valueIndex))
				)
				.map(buildDetailDto);

			const response = await saveMST4050_R01({
				headerDto: editMST4050_R01,
				detailDtoList,
				searchYm: MST4050_R01Filter.searchYm,
			});

			if (!response.data.successFlag) {
				showError(response.data.message || '저장 중 오류가 발생했습니다.');
				setLoadingSpinner(false);
				return;
			}

			const savedHeader = response.data.data.headerDto;
			const savedDetailList = response.data.data.detailDtoList ?? [];
			setBaseQty(response.data.data.baseQty ?? 0);

			// 응답으로 화면 갱신 (별도 재조회 없음, 보유량은 savedHeader.eqmQty에 반영됨)
			const isNew = !MST4050_R01List.some((row) => row.equipmentCd === savedHeader?.equipmentCd);

			if (savedHeader) {
				await refetchUpdateList(
                    isNew ? 'post' : 'patch',
                    savedHeader,
                    editMST4050_R01,
                    'equipmentCd',
                    MST4050_R01List,
                    setMST4050_R01List,
                );
				setEditMST4050_R01(savedHeader);
				setSelectedMST4050_R01(savedHeader);
			}

			setMST4050_R01DetailList(savedDetailList);
			setOriginalMST4050_R01DetailList(savedDetailList);
			setDeletedMST4050_R01DetailList([]);
			setSelectedMST4050_R01DetailList([]);
			setDetailFetchTrigger(false);

			const successMessage = `${savedHeader?.equipmentCd} 약품 저장이 완료되었습니다. (입출고 내역 ${savedDetailList.length}건, 보유량 ${(savedHeader?.eqmQty ?? 0).toLocaleString()})`;
			showSuccess(successMessage);
			setMessage(successMessage);

			await renewalAllCount();
		} catch (error: any) {
			console.error('저장 중 오류 발생:', error);
			showError(error.response?.data?.message || '저장 중 오류가 발생했습니다.');
		}

		setLoadingSpinner(false);
		setTimeout(() => setMessage(''), 5000);
	}, [
		editMST4050_R01,
		MST4050_R01DetailList,
		deletedMST4050_R01DetailList,
		originalMST4050_R01DetailList,
		MST4050_R01List,
		setMST4050_R01List,
		setSelectedMST4050_R01,
		setEditMST4050_R01,
		setMST4050_R01DetailList,
		setOriginalMST4050_R01DetailList,
		setDeletedMST4050_R01DetailList,
		setSelectedMST4050_R01DetailList,
		setDetailFetchTrigger,
		renewalAllCount,
		setMessage,
		setLoadingSpinner,
	]);

	/**
	 * 헤더 선택 시 디테일 자동 조회
	 */
	// 헤더 선택 시 상세를 조회하고, 선택 해제 시 현재 입력값을 유지하며 기본값을 보완한다.
	useEffect(() => {
		if (selectedMST4050_R01?.equipmentCd) {
			setEditMST4050_R01(selectedMST4050_R01);
			fetchDetailData(selectedMST4050_R01.equipmentCd);
		} else if (selectedMST4050_R01 === undefined) {
			const prev = useMST4050_R01Store.getState().editMST4050_R01;
			const row = defaultMST4050_R01HeaderRow();
			const pick = (cur: string | undefined, fb: string) =>
				cur != null && String(cur).trim() !== '' ? cur : fb;
			setEditMST4050_R01({
				...row,
				...prev,
				equipmentType: pick(prev.equipmentType, row.equipmentType),
				eqmStatus: pick(prev.eqmStatus, row.eqmStatus),
				compCd: pick(prev.compCd, row.compCd),
				eqmOrgcd: pick(prev.eqmOrgcd, row.eqmOrgcd),
			});
		}
	}, [selectedMST4050_R01, fetchDetailData, setEditMST4050_R01, defaultMST4050_R01HeaderRow]);

	return {
		handleExcelClick,
		handlePrintClick,
		fetchData,
		defaultMST4050_R01HeaderRow,
		defaultMST4050_R01DetailRow,
		initContainerData,
		handleCreateClick,
		handleDeleteClick,
		handleSaveClick,
	};
}
